import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Check, X, RefreshCw, AlertCircle, Search } from 'lucide-react';
import api from '../../api/client';

// Scheda CONTANTI di Movimenti bancari (28/10/2026, titolare: "il flusso contanti deve essere slegato
// dalla banca anche graficamente, come Movimenti e Satispay"). Stessi dati di sempre (BankTransaction
// sul conto di tipo CONTANTI), ma con saldo, elenco e inserimento propri; la scheda MOVIMENTI ora
// mostra solo la banca. Regole del commercialista: ogni incasso/pagamento in contanti sotto 1.000 €,
// "per cosa" e "chi" sempre scritti (prima nota senza estratto conto).

const fmt = (n) => `€${Math.abs(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MESI = ['', 'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const LIMITE_CONTANTI = 1000;
const oggi = () => new Date().toISOString().slice(0, 10);
const vuoto = () => ({ data: oggi(), verso: 'ENTRATA', importo: '', operazione: '', movimentato_da: '', categoria_id: '', note: '' });

export default function Contanti({ casse, categorie, s }) {
  const cassa = casse.find(c => c.tipo === 'CONTANTI' && c.attiva !== false);
  const [anno, setAnno] = useState(String(new Date().getFullYear()));
  const [mese, setMese] = useState('');
  const [cerca, setCerca] = useState('');
  const [righe, setRighe] = useState([]);
  const [saldo, setSaldo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carica = useCallback(async () => {
    if (!cassa) return;
    setLoading(true);
    try {
      const p = new URLSearchParams({ anno, cassa_id: cassa.id, orderBy: 'data', orderDir: 'desc' });
      if (mese) p.set('mese', mese);
      const [tx, sl] = await Promise.all([api.get('/banca/transazioni?' + p), api.get(`/banca/casse/${cassa.id}/saldo`)]);
      setRighe(tx.data || []);
      setSaldo(sl.data);
    } catch (e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [cassa, anno, mese]);
  useEffect(() => { carica(); }, [carica]);

  if (!cassa) return (
    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
      Nessun conto di tipo Contanti: crealo in CASSE E CONTI.
    </div>
  );

  const parole = cerca.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const visibili = righe.filter(t => {
    if (!parole.length) return true;
    const testo = `${t.operazione} ${t.movimentato_da || ''} ${t.note || ''} ${t.categoria?.nome || ''} ${t.registrato_da || ''} ${Math.abs(t.importo).toFixed(2)}`.toLowerCase();
    return parole.every(w => testo.includes(w.replace(',', '.')));
  });
  const entrate = visibili.filter(t => t.importo > 0).reduce((a, t) => a + t.importo, 0);
  const uscite = visibili.filter(t => t.importo < 0).reduce((a, t) => a + t.importo, 0);
  const nomi = [...new Set(righe.map(t => t.movimentato_da).filter(Boolean))].sort();
  const sopraLimite = visibili.filter(t => Math.abs(t.importo) >= LIMITE_CONTANTI).length;
  const importoForm = parseFloat(String(form?.importo || '').replace(',', '.'));
  const anni = Array.from({ length: 4 }, (_, i) => String(new Date().getFullYear() - i));

  const salva = async () => {
    if (!form.data) return alert('Inserisci la data');
    if (!(importoForm > 0)) return alert("Scrivi l'importo (sempre positivo: entrata o uscita si sceglie sopra)");
    if (!form.operazione.trim()) return alert('Scrivi per cosa è stato movimentato il denaro');
    if (!form.movimentato_da.trim()) return alert('Indica chi ha dato o preso il denaro');
    if (importoForm >= LIMITE_CONTANTI && !window.confirm(`Il commercialista chiede che ogni movimento in contanti stia sotto ${fmt(LIMITE_CONTANTI)}. Salvare comunque ${fmt(importoForm)}?`)) return;
    setSalvando(true);
    try {
      await api.post('/banca/transazioni', {
        data: form.data, cassa_id: cassa.id,
        importo: form.verso === 'USCITA' ? -importoForm : importoForm,
        operazione: form.operazione.trim(), movimentato_da: form.movimentato_da.trim(),
        categoria_id: form.categoria_id || null, note: form.note.trim() || null,
      });
      setForm(null);
      carica();
    } catch (e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  };

  const elimina = async (t) => {
    if (!window.confirm(`Eliminare il movimento in contanti "${t.operazione}" di ${fmt(t.importo)}?`)) return;
    try { await api.delete(`/banca/transazioni/${t.id}`); carica(); }
    catch (e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const cambiaCategoria = async (t, categoria_id) => {
    // i contanti nascono VALIDATI (inseriti a mano): si sblocca per cambiare categoria e si rivalida
    try {
      await api.put(`/banca/transazioni/${t.id}`, { categoria_id: categoria_id || null, ...(t.stato === 'VALIDATO' ? { stato: 'DA_VALIDARE' } : {}) });
      if (t.stato === 'VALIDATO') await api.put(`/banca/transazioni/${t.id}`, { stato: 'VALIDATO' });
      carica();
    }
    catch (e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const kpi = (valore, etichetta, colore) => (
    <div style={s.kpiSmall}>
      <span style={{ color: colore, fontWeight: '800', fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)' }}>{valore}</span>
      <span style={{ color: '#555', fontSize: 'var(--font-size-xs)', marginLeft: '6px' }}>{etichetta}</span>
    </div>
  );

  return (
    <div>
      {/* Saldo e totali */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        {saldo && kpi(`${saldo.saldo_calcolato < 0 ? '−' : ''}${fmt(saldo.saldo_calcolato)}`, 'IN CASSA OGGI', saldo.saldo_calcolato < 0 ? '#EF4444' : 'var(--text)')}
        {kpi(fmt(entrate), 'ENTRATE', '#22C55E')}
        {kpi(fmt(uscite), 'USCITE', '#EF4444')}
        {kpi(visibili.length, 'MOVIMENTI', 'var(--text)')}
        <div style={{ flex: 1 }} />
        <button style={s.btnPri} onClick={() => setForm(f => f ? null : vuoto())}><Plus size={13} /> NUOVO MOVIMENTO</button>
      </div>
      {saldo?.saldo_calcolato < 0 && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '10px 14px', borderRadius: '8px', background: '#EF444415', color: '#DC2626', fontSize: 'var(--font-size-sm)', marginBottom: '14px' }}>
          <AlertCircle size={15} /> In cassa risulta meno di zero: manca un'entrata (es. un prelievo dalla banca) o un'uscita è stata segnata due volte.
        </div>
      )}

      {/* Nuovo movimento */}
      {form && (
        <div style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div style={s.cardTitle}><Plus size={14} style={{ marginRight: '6px' }} /> MOVIMENTO IN CONTANTI</div>
            <button style={s.iconBtn} onClick={() => setForm(null)}><X size={14} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={s.label}>ENTRATA O USCITA</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[['ENTRATA', '#22C55E', 'Entrata'], ['USCITA', '#EF4444', 'Uscita']].map(([v, c, l]) => (
                  <button key={v} type="button" onClick={() => setForm(f => ({ ...f, verso: v }))}
                    style={{ ...s.btnSec, flex: 1, justifyContent: 'center', ...(form.verso === v ? { background: c + '20', color: c, borderColor: c } : {}) }}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <label style={s.label}>DATA *</label>
              <input type="date" style={s.inp} value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} />
            </div>
            <div>
              <label style={s.label}>IMPORTO € *</label>
              <input type="number" step="0.01" min="0" inputMode="decimal" style={s.inp} placeholder="es. 120" value={form.importo} onChange={e => setForm(f => ({ ...f, importo: e.target.value }))} />
            </div>
          </div>
          {importoForm >= LIMITE_CONTANTI && (
            <div style={{ color: '#D97706', fontSize: 'var(--font-size-xs)', marginBottom: '10px' }}>
              Attenzione: il commercialista chiede che ogni incasso o pagamento in contanti stia sotto {fmt(LIMITE_CONTANTI)}.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={s.label}>PER COSA *</label>
              <input type="text" style={s.inp} placeholder="es. Iscrizioni torneo 12/10, acqua per il bar…" value={form.operazione} onChange={e => setForm(f => ({ ...f, operazione: e.target.value }))} />
            </div>
            <div>
              <label style={s.label}>CHI HA DATO / PRESO IL DENARO *</label>
              <input type="text" style={s.inp} list="contanti-nomi" placeholder="es. Marco (allenatore)" value={form.movimentato_da} onChange={e => setForm(f => ({ ...f, movimentato_da: e.target.value }))} />
              <datalist id="contanti-nomi">{nomi.map(n => <option key={n} value={n} />)}</datalist>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', marginBottom: '14px' }}>
            <div>
              <label style={s.label}>CATEGORIA</label>
              <select style={s.inp} value={form.categoria_id} onChange={e => setForm(f => ({ ...f, categoria_id: e.target.value }))}>
                <option value="">— nessuna —</option>
                {categorie.filter(c => c.tipo === 'ENTRAMBI' || c.tipo === form.verso).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>NOTE</label>
              <input type="text" style={s.inp} placeholder="Dettagli in più (facoltativo)" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button style={s.btnSec} onClick={() => setForm(null)}>ANNULLA</button>
            <button style={s.btnPri} onClick={salva} disabled={salvando}>
              {salvando ? <RefreshCw size={13} /> : <Check size={13} />} {salvando ? 'SALVATAGGIO…' : 'SALVA'}
            </button>
          </div>
        </div>
      )}

      {/* Filtri */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={s.filterSel} value={anno} onChange={e => setAnno(e.target.value)}>{anni.map(a => <option key={a} value={a}>{a}</option>)}</select>
        <select style={s.filterSel} value={mese} onChange={e => setMese(e.target.value)}>
          <option value="">Tutti i mesi</option>
          {MESI.slice(1).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: '320px' }}>
          <Search size={13} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
          <input style={{ ...s.filterSel, width: '100%', paddingLeft: '30px', boxSizing: 'border-box' }} placeholder="Cerca per cosa, chi, importo…" value={cerca} onChange={e => setCerca(e.target.value)} />
        </div>
        {loading && <RefreshCw size={14} style={{ color: '#888' }} />}
        {sopraLimite > 0 && <span style={{ color: '#D97706', fontSize: 'var(--font-size-xs)', fontWeight: 700 }}>{sopraLimite} movimento/i da {fmt(LIMITE_CONTANTI)} o più</span>}
      </div>

      {/* Elenco */}
      <div style={{ overflowX: 'auto', background: 'var(--bg-card,#141414)', border: '1px solid var(--border,#1E1E1E)', borderRadius: '12px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
          <thead><tr>
            <th style={s.th}>DATA</th><th style={s.th}>PER COSA</th><th style={s.th}>CHI</th>
            <th style={s.th}>CATEGORIA</th><th style={{ ...s.th, textAlign: 'right' }}>IMPORTO</th><th style={s.th}></th>
          </tr></thead>
          <tbody>
            {visibili.length === 0 && (
              <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: 'var(--text-muted)', padding: '30px' }}>
                {loading ? 'Caricamento…' : 'Nessun movimento in contanti in questo periodo.'}
              </td></tr>
            )}
            {visibili.map(t => (
              <tr key={t.id} style={{ borderTop: '1px solid var(--border,#1E1E1E)' }}>
                <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{new Date(t.data).toLocaleDateString('it-IT')}</td>
                <td style={s.td}>
                  <div style={{ fontWeight: 600 }}>{t.operazione}</div>
                  {t.note && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{t.note}</div>}
                  {t.registrato_da && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-dim,#888)' }}>registrato da {t.registrato_da}</div>}
                </td>
                <td style={s.td}>{t.movimentato_da || <span style={{ color: '#888' }}>—</span>}</td>
                <td style={{ ...s.td, minWidth: '180px' }}>
                  <select style={{ ...s.filterSel, width: '100%' }} value={t.categoria_id || ''} onChange={e => cambiaCategoria(t, e.target.value ? +e.target.value : null)}>
                    <option value="">— nessuna —</option>
                    {categorie.filter(c => c.tipo === 'ENTRAMBI' || c.tipo === (t.importo >= 0 ? 'ENTRATA' : 'USCITA') || c.id === t.categoria_id).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </td>
                <td style={{ ...s.td, textAlign: 'right', whiteSpace: 'nowrap', fontFamily: "'Barlow Condensed'", fontWeight: 800, color: t.importo > 0 ? '#22C55E' : '#EF4444' }}>
                  {t.importo > 0 ? '+' : '−'}{fmt(t.importo)}
                  {Math.abs(t.importo) >= LIMITE_CONTANTI && <div title={`Sopra il limite di ${fmt(LIMITE_CONTANTI)} chiesto dal commercialista`} style={{ fontSize: '10px', color: '#D97706' }}>≥ {fmt(LIMITE_CONTANTI)}</div>}
                </td>
                <td style={{ ...s.td, textAlign: 'right' }}>
                  <button style={s.iconBtn} title="Elimina" onClick={() => elimina(t)}><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-dim,#888)', marginTop: '10px', lineHeight: 1.5 }}>
        Il saldo "in cassa oggi" conta tutti i movimenti in contanti mai registrati. Prelievi dalla banca e versamenti in banca vanno segnati anche qui (categoria Giroconto), così entrambi i saldi tornano.
      </div>
    </div>
  );
}
